// The Team Metric block — one ROW per person who worked the ACTIVE board in scope, so the team
// reads as a single comparable table rather than a wall of cards. Rendered under both tabs; the
// same person can appear on both with different numbers, which is the point (their last-mile
// figures on Last Mile, their first-mile figures on First Mile).
//
// Presentational only: the page owns the fetch, the drawers and the click handling; this just
// reports which (person, metric) was picked. Every figure opens the list behind it — the four
// shipment counts through the shared shipment drawer, Escalations through its own; a single reason
// in the top-3 list opens that person's escalations pre-filtered to it. Every numeric column sorts,
// so "who has the most breaches today" is one click rather than a re-read.
import { Table, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import React from "react";
import { ControlTowerBoardLane, TeamMetricMember } from "../control_tower.api";
import { MetricKey } from "../types";
import styles from "../index.module.scss";

// Which figure was clicked. The four shipment metrics map onto the shared drill-down's MetricKey;
// "escalations" has its own drawer, and carries the reason when a top-3 row was the thing clicked.
export type TeamMetricClick =
  | { kind: "shipments"; member: TeamMetricMember; metric: MetricKey }
  | { kind: "grn"; member: TeamMetricMember }
  | { kind: "escalations"; member: TeamMetricMember; reason?: string };

interface Props {
  loading: boolean;
  members: TeamMetricMember[];
  boardLane: ControlTowerBoardLane; // decides which columns this board can actually fill
  onDrill: (click: TeamMetricClick) => void;
}

// A metric shown as "—" is NOT zero: first mile has no SLA rule wired up yet, so the backend sends
// null for breached / at_risk there. Rendering 0 would read as "nobody is late", which isn't known.
const SLA_UNDEFINED = "First-mile SLA isn't defined yet, so this can't be computed";

// A count cell. `accent` colours it only when non-zero, so a healthy row stays calm and the
// problems are the only thing carrying colour. A 0 is rendered flat and NOT clickable — opening a
// drawer that can only ever say "no rows" is a dead end.
const count = (v: number | null | undefined, onClick: () => void, accent?: string) => {
  if (v === null || v === undefined) {
    return (
      <Tooltip title={SLA_UNDEFINED}>
        <span className={styles.teamCellMuted}>—</span>
      </Tooltip>
    );
  }
  if (v === 0) return <span className={styles.teamCellZero}>0</span>;
  return (
    <span
      className={styles.teamCellLink}
      style={accent ? { color: accent } : undefined}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onClick()}
    >
      {v}
    </span>
  );
};

// Sorts nulls last whichever way the column is sorted, so the First Mile "—" column never
// shuffles rows around when someone clicks its header.
const byNumber = (
  key: "total_assigned" | "breached" | "at_risk" | "undelivered" | "grn_marked" | "escalations_raised"
) =>
  (a: TeamMetricMember, b: TeamMetricMember) => (a[key] ?? -1) - (b[key] ?? -1);

const columnsFor = (
  onDrill: Props["onDrill"],
  boardLane: ControlTowerBoardLane
): ColumnsType<TeamMetricMember> => {
  const columns: ColumnsType<TeamMetricMember> = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      fixed: "left",
      width: 180,
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (v: string) => <span className={styles.teamCellName}>{v}</span>,
    },
    {
      title: "Assigned",
      dataIndex: "total_assigned",
      key: "total_assigned",
      align: "center",
      width: 100,
      defaultSortOrder: "descend",
      sorter: byNumber("total_assigned"),
      render: (v: number, r) =>
        count(v, () => onDrill({ kind: "shipments", member: r, metric: "total_shipments" }), "#0958d9"),
    },
    {
      title: "Breached",
      dataIndex: "breached",
      key: "breached",
      align: "center",
      width: 100,
      sorter: byNumber("breached"),
      render: (v: number | null, r) =>
        count(v, () => onDrill({ kind: "shipments", member: r, metric: "total_breached" }), "#cf1322"),
    },
    {
      title: "At Risk",
      dataIndex: "at_risk",
      key: "at_risk",
      align: "center",
      width: 95,
      sorter: byNumber("at_risk"),
      render: (v: number | null, r) =>
        count(v, () => onDrill({ kind: "shipments", member: r, metric: "total_at_risk" }), "#d46b08"),
    },
    {
      title: (
        <Tooltip title="Assigned bookings not yet marked delivered — GRN marking is a separate step">
          <span className={styles.teamHelpHeader}>Undelivered</span>
        </Tooltip>
      ),
      dataIndex: "undelivered",
      key: "undelivered",
      align: "center",
      width: 115,
      sorter: byNumber("undelivered"),
      render: (v: number, r) =>
        count(v, () => onDrill({ kind: "shipments", member: r, metric: "total_undelivered" })),
    },
    {
      title: (
        <Tooltip title="Of their inbound bookings, how many have the GRN filed — a separate step after the truck reaches our dock">
          <span className={styles.teamHelpHeader}>GRN Marked</span>
        </Tooltip>
      ),
      dataIndex: "grn_marked",
      key: "grn_marked",
      align: "center",
      width: 115,
      sorter: byNumber("grn_marked"),
      render: (v: number | null, r) => count(v, () => onDrill({ kind: "grn", member: r }), "#389e0d"),
    },
    {
      title: (
        <Tooltip title="Escalations this person raised on this board">
          <span className={styles.teamHelpHeader}>Escalations</span>
        </Tooltip>
      ),
      dataIndex: "escalations_raised",
      key: "escalations_raised",
      align: "center",
      width: 110,
      sorter: byNumber("escalations_raised"),
      render: (v: number, r) => count(v, () => onDrill({ kind: "escalations", member: r })),
    },
    {
      // The "What's the problem?" answers behind their escalations, most common first. Left as a
      // stacked name/count list rather than tags: three long issue labels wrap badly as pills.
      title: "Top escalation reasons",
      key: "top_escalation_reasons",
      render: (_, r) =>
        r.top_escalation_reasons.length === 0 ? (
          <span className={styles.teamCellMuted}>—</span>
        ) : (
          <div className={styles.teamReasons}>
            {r.top_escalation_reasons.map((x) => (
              <div
                key={x.reason}
                className={styles.teamReasonRow}
                onClick={() => onDrill({ kind: "escalations", member: r, reason: x.reason })}
                role="button"
                tabIndex={0}
                title={`${x.reason} — open these ${x.count}`}
              >
                <span className={styles.teamReasonName}>{x.reason}</span>
                <span className={styles.teamReasonCount}>{x.count}</span>
              </div>
            ))}
          </div>
        ),
      },
  ];
  // Each board drops the columns it could never fill, rather than showing a row of em dashes:
  // First Mile has no SLA rule (so no Breached / At Risk), and Last Mile has no GRN leg.
  const hidden =
    boardLane === "first_mile" ? ["breached", "at_risk"] : ["grn_marked"];
  return columns.filter((c) => !hidden.includes(String(c.key)));
};

const TeamMetricsSection: React.FC<Props> = ({ loading, members, boardLane, onDrill }) => (
  <Table
    rowKey="user_id"
    size="small"
    loading={loading}
    columns={columnsFor(onDrill, boardLane)}
    dataSource={members}
    pagination={false}
    scroll={{ x: 1000 }}
    className={styles.teamTable}
    locale={{ emptyText: "Nobody has shipments assigned on this board in the selected scope" }}
  />
);

export default TeamMetricsSection;
